'use client';

import { useEffect, useState } from 'react';

import { parseBreachCount, sha1HexUpper } from '../lib/password-breach';

/**
 * Client-side "has this password appeared in a breach?" check against the
 * HaveIBeenPwned Pwned Passwords range API, using k-anonymity: we SHA-1 the
 * password, send only the first 5 hex chars of the hash, and match the
 * returned suffix list locally. The password itself NEVER leaves the browser.
 *
 * Advisory only — the caller renders a non-blocking warning; it does not gate
 * submit (server HIBP is disabled, so blocking here would re-introduce
 * client/server drift). Requires a secure context for `crypto.subtle`
 * (https or localhost); elsewhere it silently no-ops (`idle`).
 *
 * Docs: https://haveibeenpwned.com/API/v3#PwnedPasswords
 */
export type BreachStatus = 'idle' | 'checking' | 'safe' | 'breached' | 'error';

const MIN_LENGTH_TO_CHECK = 8;
const DEBOUNCE_MS = 500;

interface CheckedResult {
  /** The password this result was computed for (staleness guard). */
  password: string;
  status: BreachStatus;
  count: number;
}

export function usePasswordBreachCheck(password: string): {
  status: BreachStatus;
  count: number;
} {
  const [checked, setChecked] = useState<CheckedResult>({
    password: '',
    status: 'idle',
    count: 0,
  });

  useEffect(() => {
    if (password.length < MIN_LENGTH_TO_CHECK) {
      // Derived to 'idle' below — no synchronous setState in the effect body.
      return;
    }

    const controller = new AbortController();
    let active = true;

    const timer = setTimeout(() => {
      void (async () => {
        try {
          const hash = await sha1HexUpper(password);

          // Insecure context / no Web Crypto — skip quietly.
          if (!hash) {
            if (active) setChecked({ password, status: 'idle', count: 0 });
            return;
          }

          const prefix = hash.slice(0, 5);
          const suffix = hash.slice(5);

          const response = await fetch(
            `https://api.pwnedpasswords.com/range/${prefix}`,
            { signal: controller.signal },
          );

          if (!response.ok) {
            if (active) setChecked({ password, status: 'error', count: 0 });
            return;
          }

          const body = await response.text();
          const breachCount = parseBreachCount(body, suffix);

          if (!active) return;

          setChecked({
            password,
            status: breachCount > 0 ? 'breached' : 'safe',
            count: breachCount,
          });
        } catch (error) {
          if (active && (error as Error).name !== 'AbortError') {
            setChecked({ password, status: 'error', count: 0 });
          }
        }
      })();
    }, DEBOUNCE_MS);

    return () => {
      active = false;
      controller.abort();
      clearTimeout(timer);
    };
  }, [password]);

  // Derived: too-short → idle; a result for the current password → use it;
  // otherwise a check is pending (password changed since the last result).
  if (password.length < MIN_LENGTH_TO_CHECK) {
    return { status: 'idle', count: 0 };
  }

  if (checked.password === password) {
    return { status: checked.status, count: checked.count };
  }

  return { status: 'checking', count: 0 };
}
